import {
  ListingCostKey,
  ListingCostPayableAtValues,
  ListingCostPayableByValues,
} from '@ha/api/v2/types/Listing';

import {
  CostsUtilityKeys,
  CostsUtilityCompatibilityKey,
  CostsAdditionalKeys,
  CostsDepositKeys,
  CostsOptionalKeys,
} from 'ha/models/ListingCosts/types';

import {
  CostInput,
  CostDefaultOptions,
  CostGroup,
  CostGroupValues,
} from './types';

export const COST_KEYS_BY_GROUP: {
  [K in CostGroup]: ListingCostKey[];
} = {
  [CostGroupValues.UTILITY]: [
    ...Object.values(CostsUtilityKeys),
    ...Object.values(CostsUtilityCompatibilityKey),
  ],
  [CostGroupValues.ADDITIONAL]: Object.values(CostsAdditionalKeys),
  [CostGroupValues.DEPOSIT]: Object.values(CostsDepositKeys),
  [CostGroupValues.OPTIONAL]: Object.values(CostsOptionalKeys),
};

/**
 * Grouped attributes according to cost type to use below in the aggregated data
 */

const CostsOptionalPayableAtValues = [
  ListingCostPayableAtValues.MOVE_IN,
  ListingCostPayableAtValues.MOVE_OUT,
  ListingCostPayableAtValues.ONE_OFF,
  ListingCostPayableAtValues.MONTHLY,
  ListingCostPayableAtValues.BIWEEKLY,
  ListingCostPayableAtValues.WEEKLY,
];

const CostsOptionalPayableAtOneTimeValues = [
  ListingCostPayableAtValues.MOVE_IN,
  ListingCostPayableAtValues.MOVE_OUT,
  ListingCostPayableAtValues.ONE_OFF,
];

const CostUtilityInputDefaults = {
  payableBy: [
    ListingCostPayableByValues.INCLUDED_IN_RENT,
    ListingCostPayableByValues.TENANT,
  ],
  payableAt: [
    ListingCostPayableAtValues.MONTHLY,
    ListingCostPayableAtValues.BIMONTHLY,
    ListingCostPayableAtValues.BIWEEKLY,
    ListingCostPayableAtValues.WEEKLY,
  ],
  refundable: [CostDefaultOptions.FALSE],
  required: [CostDefaultOptions.TRUE],
  isEstimated: [CostDefaultOptions.TRUE, CostDefaultOptions.FALSE],
};

const CostsDepositInputDefaults = {
  payableBy: [ListingCostPayableByValues.TENANT],
  payableAt: [ListingCostPayableAtValues.MOVE_IN],
  refundable: [CostDefaultOptions.TRUE],
  required: [CostDefaultOptions.TRUE, CostDefaultOptions.FALSE],
  isEstimated: [CostDefaultOptions.FALSE],
};

const CostsOptionalInputDefaults = {
  payableBy: [
    ListingCostPayableByValues.INCLUDED_IN_RENT,
    ListingCostPayableByValues.TENANT,
  ],
  refundable: [CostDefaultOptions.FALSE],
  required: [CostDefaultOptions.FALSE],
  isEstimated: [CostDefaultOptions.FALSE],
};

/**
 * Aggregated data for use in components
 */

export const COST_INPUTS_BY_GROUP: {
  [K in CostGroup]: CostInput[];
} = {
  [CostGroupValues.UTILITY]: [
    {
      ...CostUtilityInputDefaults,
      id: CostsUtilityKeys.ELECTRICITY_BILL,
    },
    {
      ...CostUtilityInputDefaults,
      id: CostsUtilityKeys.WATER_BILL,
    },
    {
      ...CostUtilityInputDefaults,
      id: CostsUtilityKeys.GAS_BILL,
      payableBy: [
        ListingCostPayableByValues.INCLUDED_IN_RENT,
        ListingCostPayableByValues.TENANT,
        ListingCostPayableByValues.NOT_APPLICABLE,
      ],
    },
    {
      ...CostUtilityInputDefaults,
      id: CostsUtilityKeys.INTERNET_BILL,
      payableBy: [
        ListingCostPayableByValues.INCLUDED_IN_RENT,
        ListingCostPayableByValues.TENANT,
        ListingCostPayableByValues.NOT_APPLICABLE,
      ],
    },
    {
      ...CostUtilityInputDefaults,
      id: CostsUtilityKeys.BROADCASTING,
      required: [CostDefaultOptions.FALSE],
      payableBy: [
        ListingCostPayableByValues.INCLUDED_IN_RENT,
        ListingCostPayableByValues.TENANT,
        ListingCostPayableByValues.NOT_APPLICABLE,
      ],
    },
    {
      id: CostsUtilityCompatibilityKey.UTILITY_BILLS,
      payableBy: [ListingCostPayableByValues.TENANT],
      payableAt: [ListingCostPayableAtValues.MONTHLY],
      refundable: [CostDefaultOptions.FALSE],
      required: [CostDefaultOptions.TRUE],
      isEstimated: [CostDefaultOptions.TRUE],
    },
  ],
  [CostGroupValues.ADDITIONAL]: [
    {
      id: CostsAdditionalKeys.ADMINISTRATION_FEE,
      payableAt: [
        ListingCostPayableAtValues.MOVE_IN,
        ListingCostPayableAtValues.MOVE_OUT,
      ],
      payableBy: [ListingCostPayableByValues.TENANT],
      required: [CostDefaultOptions.TRUE],
      refundable: [CostDefaultOptions.FALSE],
      isEstimated: [CostDefaultOptions.FALSE],
    },
    {
      id: CostsAdditionalKeys.CLEANING_FEE,
      payableAt: [
        ListingCostPayableAtValues.MOVE_IN,
        ListingCostPayableAtValues.MOVE_OUT,
        ListingCostPayableAtValues.MONTHLY,
        ListingCostPayableAtValues.BIMONTHLY,
      ],
      payableBy: [ListingCostPayableByValues.TENANT],
      required: [CostDefaultOptions.TRUE],
      refundable: [CostDefaultOptions.FALSE],
      isEstimated: [CostDefaultOptions.FALSE],
    },
    {
      id: CostsAdditionalKeys.OTHER_ADDITIONAL_COSTS,
      payableBy: [
        ListingCostPayableByValues.INCLUDED_IN_RENT,
        ListingCostPayableByValues.TENANT,
      ],
      payableAt: [
        ListingCostPayableAtValues.MOVE_IN,
        ListingCostPayableAtValues.MOVE_OUT,
        ListingCostPayableAtValues.MONTHLY,
        ListingCostPayableAtValues.BIMONTHLY,
        ListingCostPayableAtValues.BIWEEKLY,
        ListingCostPayableAtValues.WEEKLY,
      ],
      required: [CostDefaultOptions.TRUE],
      refundable: [CostDefaultOptions.FALSE],
      isEstimated: [CostDefaultOptions.FALSE],
    },
  ],
  [CostGroupValues.DEPOSIT]: [
    {
      ...CostsDepositInputDefaults,
      id: CostsDepositKeys.SECURITY_DEPOSIT,
    },
    {
      ...CostsDepositInputDefaults,
      id: CostsDepositKeys.BEDDING_DEPOSIT,
    },
    {
      ...CostsDepositInputDefaults,
      id: CostsDepositKeys.TOWEL_DEPOSIT,
    },
    {
      ...CostsDepositInputDefaults,
      id: CostsDepositKeys.OTHER_DEPOSIT,
    },
  ],
  [CostGroupValues.OPTIONAL]: [
    {
      ...CostsOptionalInputDefaults,
      id: CostsOptionalKeys.BIKE_RENT,
      payableAt: CostsOptionalPayableAtValues,
    },
    {
      ...CostsOptionalInputDefaults,
      id: CostsOptionalKeys.CLEANING_SERVICE,
      payableAt: CostsOptionalPayableAtValues,
    },
    {
      ...CostsOptionalInputDefaults,
      id: CostsOptionalKeys.EARLY_MOVE_IN,
      payableAt: CostsOptionalPayableAtOneTimeValues,
    },
    {
      ...CostsOptionalInputDefaults,
      id: CostsOptionalKeys.EARLY_MOVE_OUT,
      payableAt: CostsOptionalPayableAtOneTimeValues,
    },
    {
      ...CostsOptionalInputDefaults,
      id: CostsOptionalKeys.FINAL_CLEANING,
      payableAt: CostsOptionalPayableAtOneTimeValues,
    },
    {
      ...CostsOptionalInputDefaults,
      id: CostsOptionalKeys.FULL_BOARD,
      payableAt: CostsOptionalPayableAtValues,
    },
    {
      ...CostsOptionalInputDefaults,
      id: CostsOptionalKeys.GYM,
      payableAt: CostsOptionalPayableAtValues,
    },
    {
      ...CostsOptionalInputDefaults,
      id: CostsOptionalKeys.HALF_BOARD,
      payableAt: CostsOptionalPayableAtValues,
    },
    {
      ...CostsOptionalInputDefaults,
      id: CostsOptionalKeys.LATE_MOVE_IN,
      payableAt: CostsOptionalPayableAtOneTimeValues,
    },
    {
      ...CostsOptionalInputDefaults,
      id: CostsOptionalKeys.LATE_MOVE_OUT,
      payableAt: CostsOptionalPayableAtOneTimeValues,
    },
    {
      ...CostsOptionalInputDefaults,
      id: CostsOptionalKeys.OVERNIGHT_GUESTS,
      payableAt: CostsOptionalPayableAtOneTimeValues,
    },
    {
      ...CostsOptionalInputDefaults,
      id: CostsOptionalKeys.OTHER_OPTIONAL_COSTS,
      payableAt: CostsOptionalPayableAtValues,
    },
    {
      ...CostsOptionalInputDefaults,
      id: CostsOptionalKeys.PARKING,
      payableAt: CostsOptionalPayableAtValues,
    },
    {
      ...CostsOptionalInputDefaults,
      id: CostsOptionalKeys.TOWEL_BEDDING,
      payableAt: CostsOptionalPayableAtValues,
    },
    {
      ...CostsOptionalInputDefaults,
      id: CostsOptionalKeys.END_EARLY,
      payableAt: [
        ListingCostPayableAtValues.MOVE_OUT,
        ListingCostPayableAtValues.ONE_OFF,
      ],
    },
  ],
};

export const SHOW_ESTIMATED_COSTS = [
  ...Object.values(CostsUtilityKeys),
  'utility-bills',
];
